import { authFetch } from "@/utils/api";

/**
 * Packages API of "Agentes do escritório" (docs/plans/PLAN_legal-service-agents.md, Phase 2).
 */

export const packagesQueryKey = (accountId) => ["agent-skills", "packages", accountId];
export const packageSetupQueryKey = (accountId, packageId) => ["agent-skills", "package-setup", accountId, packageId];

/** A refused apply: `code` and `details` drive the form (field errors, blocked items, manual edits). */
export class ApplyRequestError extends Error {
  constructor(message, { status, code = null, details = {} } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApplyRequestError(data.error || "Não foi possível concluir agora.", {
      status: res.status,
      code: data.code || null,
      details: data.details || {},
    });
  }
  return data;
}

export async function fetchPackages(accountId) {
  return readJson(await authFetch(`/api/internal/agent-skills/${accountId}/packages`));
}

export async function fetchPackageSetup(accountId, packageId) {
  return readJson(await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/setup`));
}

export async function applyPackage(accountId, packageId, { skills, overwriteManualEdits = false, guide = null }) {
  return readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/apply`, {
      method: "POST",
      body: JSON.stringify({ skills, overwriteManualEdits, ...(guide ? { guide } : {}) }),
    }),
  );
}

// ── Setup assistant (Phase 3) ────────────────────────────────────

export const assistantQueryKey = (accountId, packageId) => ["agent-skills", "assistant", accountId, packageId];

export async function fetchAssistantSession(accountId, packageId) {
  const data = await readJson(await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/assistant`));
  return data.session;
}

export async function sendAssistantMessage(accountId, packageId, message) {
  return readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/assistant`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),
  );
}

export async function resetAssistantSession(accountId, packageId) {
  return readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/assistant/reset`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  );
}

// ── Setup wizard ─────────────────────────────────────────────────

/** Example of how the agent opens a conversation with the answers so far (no model call). */
export async function fetchPreview(accountId, params) {
  const data = await readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/preview`, {
      method: "POST",
      body: JSON.stringify({ params }),
    }),
  );
  return data.messages || [];
}

/**
 * "Usar meu roteiro": the firm's text and/or one file (PDF, Word, texto ou
 * áudio) become a plan of agents to review.
 * @returns {Promise<{plan: object, document: {name: string, indexed: boolean}|null, quota: object}>}
 */
export async function planGuide(accountId, { text, file }) {
  const form = new FormData();
  if (text) form.append("text", text);
  if (file) form.append("file", file);
  return readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/guide`, { method: "POST", body: form }),
  );
}

/** What the reminder or birthday dialog offers: channels, presets, schedule, task types. */
export const automationSetupQueryKey = (accountId, workflow) => ["agent-skills", "automation-setup", accountId, workflow];

export async function fetchAutomationSetup(accountId, workflow) {
  return readJson(await authFetch(`/api/internal/agent-skills/${accountId}/automations/${workflow}/setup`));
}

/** Creates the template on Meta and the automation (paused until the approval). */
export async function createQuickAutomation(accountId, workflow, { channelId, presetId, header, file, taskTypeIds }) {
  const form = new FormData();
  if (channelId) form.append("channelId", channelId);
  form.append("presetId", presetId);
  form.append("header", header);
  if (file && header !== "none") form.append("file", file);
  if (taskTypeIds) form.append("taskTypeIds", JSON.stringify(taskTypeIds));
  return readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/automations/${workflow}`, { method: "POST", body: form }),
  );
}

/**
 * "Conversar com o agente": the reception agent's next reply to a test
 * conversation, with the answers (and roteiro) given so far. Nothing is saved.
 * @param {{params: object, guide?: object|null, messages: {from: 'contact'|'agent', text: string}[]}} input
 * @returns {Promise<string>}
 */
export async function tryAgent(accountId, packageId, { params, guide = null, messages }) {
  const data = await readJson(
    await authFetch(`/api/internal/agent-skills/${accountId}/packages/${packageId}/try`, {
      method: "POST",
      body: JSON.stringify({ params, guide, messages }),
    }),
  );
  return data.reply;
}
